import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  Button,
  Input,
  Select,
  Switch,
  Tooltip,
} from "@chakra-ui/react";
import Decimal from "decimal.js";
import toast from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import {
  IoArrowBackOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";
import { saleOpeningEventMechanismParametersCall } from "../../api/saleOpeningEventMechanismParameters";
import { TextareaControl } from "../../components/form/formControls";
import { LevelCalculatorRateK } from "../../utils/contants";

const CVKD_RATE_MODE = {
  INCLUDED_ELITE: "INCLUDED_ELITE",
  NOT_INCLUDED_ELITE: "NOT_INCLUDED_ELITE",
};

const DEFAULT_MAIN_MECHANISM_RATE = 5;
const DEFAULT_CVKD_MECHANISM_RATE = 2;
const DEFAULT_PDV_RATE = 1.5;
const DEFAULT_ELITE_RATE = 0.2;
const DEFAULT_CHUNG_ELITE_DIVISOR = 1.25;
const DEFAULT_TRANSACTION_RATE = 100;
const AUTO_STATUS_K_RATE = 1;

const VAT_DIVISOR = 1.1;

const CHUNG_CODE = "CHUNG";
const CVKD_CODE = "CVKD";
const CVKD_ELITE_CODE = "CVKD_ELITE";
const TOTAL_CODE = "TOTAL";
const REMAIN_CODE = "REMAIN";

const SUMMARY_CODES = [TOTAL_CODE, REMAIN_CODE];

const FORMULA_FIELDS = [
  "mechanismRate",
  "rate",
  "mechanismRateElite",
  "rateElite",
];

const DISPLAY_DECIMAL = {
  mechanismRate: 4,
  rate: 4,
  mechanismRateElite: 4,
  rateElite: 4,
};

const schemaAdd = Yup.object().shape({
  saleOpeningEventId: Yup.string().required("mã dự án là bắt buộc"),
  mechanismReceive: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string" && originalValue.trim() === "") {
        return null;
      }
      return Number(originalValue);
    })
    .typeError("Cơ chế nhận về phải là một số hợp lệ")
    .min(0, "Cơ chế nhận về phải lớn hơn hoặc bằng 0")
    .max(100, "Cơ chế nhận về phải nhỏ hơn hoặc bằng 100")
    .required("Vui lòng nhập cơ chế nhận về"),
  cvkdMechanismRate: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string" && originalValue.trim() === "") {
        return null;
      }
      return Number(originalValue);
    })
    .typeError("Cơ chế CVKD phải là một số hợp lệ")
    .min(0, "Cơ chế CVKD phải lớn hơn hoặc bằng 0")
    .max(100, "Cơ chế CVKD phải nhỏ hơn hoặc bằng 100")
    .required("Vui lòng nhập cơ chế CVKD"),
});

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

const d = (value, fallback = 0) => {
  if (!hasValue(value)) {
    return new Decimal(fallback);
  }

  try {
    const decimal = new Decimal(value);
    return decimal.isFinite() ? decimal : new Decimal(fallback);
  } catch {
    return new Decimal(fallback);
  }
};

const rawNumber = (value, fallback = 0) => d(value, fallback).toNumber();

const safeDivide = (a, b, fallback = 0) => {
  const denominator = d(b);

  if (denominator.equals(0)) {
    return d(fallback);
  }

  return d(a).div(denominator);
};

const getChungMechanismRateEliteForCalc = (value, applyVat) => {
  const displayValue = d(value);

  if (!applyVat) {
    return displayValue;
  }

  return safeDivide(displayValue, VAT_DIVISOR);
};

const clampPercent = (value) => {
  if (value === "") return "";

  const decimal = d(value);

  if (decimal.lessThan(0)) return 0;
  if (decimal.greaterThan(100)) return 100;

  return decimal.toNumber();
};

const toDisplayNumber = (value, dp = 4) => {
  if (!hasValue(value)) return "";

  return d(value).toDecimalPlaces(dp).toNumber().toLocaleString("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: dp,
  });
};

const toInputDisplayValue = (value, field) => {
  if (!hasValue(value)) return "";

  const dp = DISPLAY_DECIMAL[field] ?? 4;

  return d(value).toDecimalPlaces(dp).toNumber();
};

const percentText = (value, dp = 2) => {
  if (!hasValue(value)) return "";

  return `${toDisplayNumber(value, dp)}%`;
};

const numberText = (value, dp = 4) => {
  if (!hasValue(value)) return "";

  return toDisplayNumber(value, dp);
};

const normalizeCode = (code) => {
  if (code === "P.TGĐ") return "PTGD";
  return code || "";
};

const makeManualKey = (code, field) => `${code}.${field}`;

const isManualCell = (manualCells, code, field) =>
  Boolean(manualCells?.[makeManualKey(code, field)]);

const normalizeRow = (item, index) => ({
  id: item?.id || item?._id || `${normalizeCode(item?.code) || "ROW"}_${index}`,
  name: item?.name || "",
  code: normalizeCode(item?.code),
  mechanismRate: hasValue(item?.mechanismRate)
    ? rawNumber(item.mechanismRate)
    : "",
  rate: hasValue(item?.rate) ? rawNumber(item.rate) : "",
  mechanismRateElite: hasValue(item?.mechanismRateElite)
    ? rawNumber(item.mechanismRateElite)
    : "",
  rateElite: hasValue(item?.rateElite) ? rawNumber(item.rateElite) : "",
});

const buildDefaultRows = () => {
  const source =
    Array.isArray(LevelCalculatorRateK) && LevelCalculatorRateK.length > 0
      ? LevelCalculatorRateK
      : [
          {
            id: "CHUNG",
            name: "Cơ chế nhận về",
            code: "CHUNG",
            mechanismRate: 5,
            rate: 100,
            mechanismRateElite: 4,
            rateElite: 100,
          },
          {
            id: "CVKD",
            name: "Chuyên viên kinh doanh",
            code: "CVKD",
            mechanismRate: 1.5,
            rate: 30,
            mechanismRateElite: 2,
            rateElite: 50,
          },
          {
            id: "CVKD_ELITE",
            name: "CVKD (phần Elite)",
            code: "CVKD_ELITE",
            mechanismRate: 0.2,
            rate: 4,
            mechanismRateElite: "",
            rateElite: "",
          },
          {
            id: "GDK",
            name: "Giám đốc khối",
            code: "GDK",
            mechanismRate: 0.5,
            rate: 10,
            mechanismRateElite: 0.22,
            rateElite: 5.6,
          },
          {
            id: "GDV",
            name: "Giám đốc vùng",
            code: "GDV",
            mechanismRate: 0.3,
            rate: 6,
            mechanismRateElite: 0.13,
            rateElite: 3.3,
          },
          {
            id: "TKD",
            name: "Thưởng kinh doanh",
            code: "TKD",
            mechanismRate: 0.25,
            rate: 5,
            mechanismRateElite: 0.11,
            rateElite: 2.8,
          },
          {
            id: "PTDA",
            name: "Phát triển dự án",
            code: "PTDA",
            mechanismRate: 0.15,
            rate: 3,
            mechanismRateElite: 0.07,
            rateElite: 1.7,
          },
          {
            id: "GTDA",
            name: "Giới thiệu dự án",
            code: "GTDA",
            mechanismRate: 0.05,
            rate: 1,
            mechanismRateElite: 0.02,
            rateElite: 0.6,
          },
          {
            id: "TGD",
            name: "Tổng giám đốc",
            code: "TGD",
            mechanismRate: 0.2,
            rate: 4,
            mechanismRateElite: 0.09,
            rateElite: 2.2,
          },
          {
            id: "BE",
            name: "Khối BE",
            code: "BE",
            mechanismRate: 0.1,
            rate: 2,
            mechanismRateElite: 0.04,
            rateElite: 1.1,
          },
          {
            id: "GDCN",
            name: "Giám đốc chi nhánh",
            code: "GDCN",
            mechanismRate: 0.15,
            rate: 3,
            mechanismRateElite: 0.07,
            rateElite: 1.7,
          },
          {
            id: "PTGD",
            name: "Phó tổng giám đốc",
            code: "PTGD",
            mechanismRate: 0.1,
            rate: 2,
            mechanismRateElite: 0.04,
            rateElite: 1.1,
          },
          {
            id: "TOTAL",
            name: "Tổng cộng",
            code: "TOTAL",
            mechanismRate: 3.5,
            rate: 70,
            mechanismRateElite: 2.8,
            rateElite: 70,
          },
          {
            id: "REMAIN",
            name: "Còn lại",
            code: "REMAIN",
            mechanismRate: 1.5,
            rate: 30,
            mechanismRateElite: 1.2,
            rateElite: 30,
          },
        ];

  return source.map(normalizeRow);
};

const getRow = (rows, code) => rows.find((item) => item.code === code);

const isSummaryRow = (code) => SUMMARY_CODES.includes(code);

const isBaseRow = (code) =>
  [CHUNG_CODE, TOTAL_CODE, REMAIN_CODE].includes(code);

const isAutoEliteTargetRow = (code) =>
  ![CHUNG_CODE, CVKD_CODE, CVKD_ELITE_CODE, TOTAL_CODE, REMAIN_CODE].includes(
    code,
  );

// Khi bật "Tự động tính", chỉ 2 ô cần người dùng nhập -> highlight viền đỏ:
// ô "Cơ chế %" (cột Áp dụng cho CVKD Elite) của dòng CHUNG và dòng CVKD.
const isRequiredInputCell = (code, field) =>
  field === "mechanismRateElite" && (code === CHUNG_CODE || code === CVKD_CODE);

const inputCellStyle = {
  h: "28px",
  minW: "72px",
  type: "number",
  step: "any",
  bg: "white",
  borderColor: "#CBD5E0",
  textAlign: "left",
  fontSize: "12px",
  px: "6px",
};

const editableCell = ({ field, value, onChange, highlight }) => (
  <Input
    {...inputCellStyle}
    {...(highlight ? { borderColor: "red.500", borderWidth: "2px" } : {})}
    value={toInputDisplayValue(value, field)}
    onChange={(event) => onChange(event.target.value)}
  />
);

const setFormulaValue = ({ row, field, value, autoCalculate, manualCells }) => {
  if (!autoCalculate) return row;

  if (isManualCell(manualCells, row.code, field)) return row;

  return {
    ...row,
    [field]: rawNumber(value),
  };
};

const sumRowsByField = (rows, field) =>
  rows.reduce((acc, item) => acc.plus(d(item[field])), new Decimal(0));

const calculateSummaryOnly = ({ rows, cvkdRateMode }) => {
  const cvkd = getRow(rows, CVKD_CODE);
  const cvkdElite = getRow(rows, CVKD_ELITE_CODE);
  const gdk = getRow(rows, "GDK");

  const eliteDefault = hasValue(cvkdElite?.mechanismRate)
    ? d(cvkdElite.mechanismRate)
    : d(DEFAULT_ELITE_RATE);

  const kRate =
    cvkdRateMode === CVKD_RATE_MODE.NOT_INCLUDED_ELITE
      ? safeDivide(
          d(cvkdElite?.mechanismRateElite),
          d(cvkdElite?.mechanismRate),
        )
      : safeDivide(d(gdk?.mechanismRateElite), d(gdk?.mechanismRate));

  const hoaHong = kRate.mul(DEFAULT_PDV_RATE);
  const elite = kRate.mul(eliteDefault);
  const totalPDVAndTN = d(cvkd?.mechanismRateElite);

  const thuongNong =
    cvkdRateMode === CVKD_RATE_MODE.NOT_INCLUDED_ELITE
      ? Decimal.max(totalPDVAndTN.minus(hoaHong), 0)
      : Decimal.max(totalPDVAndTN.minus(hoaHong).minus(elite), 0);

  return {
    kRate: kRate.toNumber(),
    hoaHong: hoaHong.toNumber(),
    elite: elite.toNumber(),
    thuongNong: thuongNong.toNumber(),
    totalPDVAndTN: totalPDVAndTN.toNumber(),
    ratePDVAndTNCVKDElite: totalPDVAndTN.toNumber(),
    rateDefault: DEFAULT_PDV_RATE,
    rateEliteDefault: eliteDefault.toNumber(),
    transactionRate: DEFAULT_TRANSACTION_RATE,
  };
};

const calculateMechanism = ({
  inputRows,
  mechanismReceive,
  cvkdMechanismRate,
  cvkdRateMode,
  autoCalculate,
  manualCells = {},
  applyChungEliteVat = false,
}) => {
  let nextRows = inputRows.map((item, index) => normalizeRow(item, index));

  if (!autoCalculate) {
    return {
      rows: nextRows,
      summary: calculateSummaryOnly({
        rows: nextRows,
        cvkdRateMode,
      }),
    };
  }

  const currentChung = getRow(nextRows, CHUNG_CODE);
  const currentCVKD = getRow(nextRows, CVKD_CODE);

  const mainMechanism = d(
    mechanismReceive ?? currentChung?.mechanismRate,
    DEFAULT_MAIN_MECHANISM_RATE,
  );

  const chungRate = d(currentChung?.rate, DEFAULT_TRANSACTION_RATE);

  const chungEliteMechanismDisplay = hasValue(currentChung?.mechanismRateElite)
    ? d(currentChung.mechanismRateElite)
    : safeDivide(mainMechanism, DEFAULT_CHUNG_ELITE_DIVISOR);

  const chungEliteMechanismForCalc = getChungMechanismRateEliteForCalc(
    chungEliteMechanismDisplay,
    applyChungEliteVat,
  );

  const chungRateElite = d(currentChung?.rateElite, DEFAULT_TRANSACTION_RATE);

  const cvkdInput = d(
    cvkdMechanismRate ?? currentCVKD?.mechanismRateElite,
    DEFAULT_CVKD_MECHANISM_RATE,
  );

  nextRows = nextRows.map((item) => {
    if (item.code === CHUNG_CODE) {
      let nextItem = { ...item };

      nextItem = setFormulaValue({
        row: nextItem,
        field: "mechanismRate",
        value: mainMechanism,
        autoCalculate,
        manualCells,
      });

      nextItem = setFormulaValue({
        row: nextItem,
        field: "mechanismRateElite",
        value: chungEliteMechanismDisplay,
        autoCalculate,
        manualCells,
      });

      if (!hasValue(nextItem.rate)) {
        nextItem = setFormulaValue({
          row: nextItem,
          field: "rate",
          value: DEFAULT_TRANSACTION_RATE,
          autoCalculate,
          manualCells,
        });
      }

      if (!hasValue(nextItem.rateElite)) {
        nextItem = setFormulaValue({
          row: nextItem,
          field: "rateElite",
          value: DEFAULT_TRANSACTION_RATE,
          autoCalculate,
          manualCells,
        });
      }

      return nextItem;
    }

    if (item.code === CVKD_CODE) {
      let nextItem = { ...item };

      nextItem = setFormulaValue({
        row: nextItem,
        field: "mechanismRateElite",
        value: cvkdInput,
        autoCalculate,
        manualCells,
      });

      if (
        !isManualCell(manualCells, CVKD_CODE, "rate") &&
        hasValue(nextItem.mechanismRate)
      ) {
        nextItem = {
          ...nextItem,
          rate: safeDivide(nextItem.mechanismRate, mainMechanism)
            .mul(chungRate)
            .toNumber(),
        };
      }

      if (
        !isManualCell(manualCells, CVKD_CODE, "rateElite") &&
        hasValue(nextItem.mechanismRateElite)
      ) {
        nextItem = {
          ...nextItem,
          rateElite: safeDivide(
            nextItem.mechanismRateElite,
            chungEliteMechanismForCalc,
          )
            .mul(chungRateElite)
            .toNumber(),
        };
      }

      return nextItem;
    }

    if (item.code === CVKD_ELITE_CODE) {
      let nextItem = { ...item };

      if (
        !isManualCell(manualCells, CVKD_ELITE_CODE, "rate") &&
        hasValue(nextItem.mechanismRate)
      ) {
        nextItem = {
          ...nextItem,
          rate: safeDivide(nextItem.mechanismRate, mainMechanism)
            .mul(chungRate)
            .toNumber(),
        };
      }

      if (cvkdRateMode === CVKD_RATE_MODE.INCLUDED_ELITE) {
        if (!isManualCell(manualCells, CVKD_ELITE_CODE, "rateElite")) {
          nextItem = {
            ...nextItem,
            rateElite: 0,
          };
        }

        if (!isManualCell(manualCells, CVKD_ELITE_CODE, "mechanismRateElite")) {
          nextItem = {
            ...nextItem,
            mechanismRateElite: 0,
          };
        }
      }

      return nextItem;
    }

    return item;
  });

  const chung = getRow(nextRows, CHUNG_CODE);
  const cvkd = getRow(nextRows, CVKD_CODE);
  const cvkdElite = getRow(nextRows, CVKD_ELITE_CODE);

  const nextChungRate = d(chung?.rate, DEFAULT_TRANSACTION_RATE);
  const nextChungRateElite = d(chung?.rateElite, DEFAULT_TRANSACTION_RATE);

  const nextChungEliteMechanismDisplay = d(
    chung?.mechanismRateElite,
    chungEliteMechanismDisplay,
  );

  const nextChungEliteMechanismForCalc = getChungMechanismRateEliteForCalc(
    nextChungEliteMechanismDisplay,
    applyChungEliteVat,
  );

  const normalRows = nextRows.filter((item) => !isBaseRow(item.code));

  const totalNormalRateBeforeAuto = sumRowsByField(normalRows, "rate");
  const cvkdRate = d(cvkd?.rate);
  const cvkdRateElite = d(cvkd?.rateElite);
  const cvkdEliteRate = d(cvkdElite?.rate);
  const cvkdMechanismElite = d(cvkd?.mechanismRateElite);

  if (cvkdRateMode === CVKD_RATE_MODE.NOT_INCLUDED_ELITE) {
    nextRows = nextRows.map((item) => {
      if (item.code !== CVKD_ELITE_CODE) return item;

      let nextItem = { ...item };

      if (!isManualCell(manualCells, CVKD_ELITE_CODE, "rateElite")) {
        const calculatedRateElite = safeDivide(
          d(item.rate).mul(totalNormalRateBeforeAuto.minus(cvkdRateElite)),
          totalNormalRateBeforeAuto.minus(cvkdRate),
        );

        nextItem = {
          ...nextItem,
          rateElite: Decimal.max(calculatedRateElite, 0).toNumber(),
        };
      }

      if (!isManualCell(manualCells, CVKD_ELITE_CODE, "mechanismRateElite")) {
        const calculatedMechanismRateElite = safeDivide(
          d(nextItem.rateElite).mul(cvkdMechanismElite),
          cvkdRateElite,
        );

        nextItem = {
          ...nextItem,
          mechanismRateElite: Decimal.max(
            calculatedMechanismRateElite,
            0,
          ).toNumber(),
        };
      }

      return nextItem;
    });
  }

  nextRows = nextRows.map((item) => {
    if (!isAutoEliteTargetRow(item.code)) {
      return item;
    }

    let nextItem = { ...item };

    if (!isManualCell(manualCells, item.code, "rateElite")) {
      const denominator =
        cvkdRateMode === CVKD_RATE_MODE.NOT_INCLUDED_ELITE
          ? totalNormalRateBeforeAuto.minus(cvkdRate)
          : totalNormalRateBeforeAuto.minus(cvkdRate).minus(cvkdEliteRate);

      const calculatedRateElite = safeDivide(
        d(item.rate).mul(totalNormalRateBeforeAuto.minus(cvkdRateElite)),
        denominator,
      );

      nextItem = {
        ...nextItem,
        rateElite: Decimal.max(calculatedRateElite, 0).toNumber(),
      };
    }

    if (!isManualCell(manualCells, item.code, "mechanismRateElite")) {
      const calculatedMechanismRateElite = safeDivide(
        d(nextItem.rateElite).mul(cvkdMechanismElite),
        cvkdRateElite,
      );

      nextItem = {
        ...nextItem,
        mechanismRateElite: Decimal.max(
          calculatedMechanismRateElite,
          0,
        ).toNumber(),
      };
    }

    return nextItem;
  });

  const rowsForTotal = nextRows.filter(
    (item) => ![CHUNG_CODE, TOTAL_CODE, REMAIN_CODE].includes(item.code),
  );

  const total = {
    mechanismRate: sumRowsByField(rowsForTotal, "mechanismRate"),
    rate: sumRowsByField(rowsForTotal, "rate"),
    mechanismRateElite: sumRowsByField(rowsForTotal, "mechanismRateElite"),
    rateElite: sumRowsByField(rowsForTotal, "rateElite"),
  };

  const autoTotal = {
    mechanismRate: total.mechanismRate.toNumber(),
    rate: total.rate.toNumber(),
    mechanismRateElite: total.mechanismRateElite.toNumber(),
    rateElite: total.rateElite.toNumber(),
  };

  const autoRemain = {
    mechanismRate: Decimal.max(
      mainMechanism.minus(total.mechanismRate),
      0,
    ).toNumber(),
    rate: Decimal.max(nextChungRate.minus(total.rate), 0).toNumber(),
    mechanismRateElite: Decimal.max(
      nextChungEliteMechanismForCalc.mul(0.3),
      0,
    ).toNumber(),
    rateElite: Decimal.max(nextChungRateElite.mul(0.3), 0).toNumber(),
  };

  nextRows = nextRows.map((item) => {
    if (item.code === TOTAL_CODE) {
      let nextItem = { ...item };

      FORMULA_FIELDS.forEach((field) => {
        nextItem = setFormulaValue({
          row: nextItem,
          field,
          value: autoTotal[field],
          autoCalculate,
          manualCells,
        });
      });

      return nextItem;
    }

    if (item.code === REMAIN_CODE) {
      let nextItem = { ...item };

      FORMULA_FIELDS.forEach((field) => {
        nextItem = setFormulaValue({
          row: nextItem,
          field,
          value: autoRemain[field],
          autoCalculate,
          manualCells,
        });
      });

      return nextItem;
    }

    return item;
  });

  return {
    rows: nextRows,
    summary: calculateSummaryOnly({
      rows: nextRows,
      cvkdRateMode,
    }),
  };
};

const TableUpdateProjectMechanism = ({
  saleOpeningEventId,
  onClose,
  id,
  onToggle,
  refetch,
  name,
}) => {
  const [rows, setRows] = useState([]);
  const [cvkdRateMode, setCvkdRateMode] = useState(
    CVKD_RATE_MODE.INCLUDED_ELITE,
  );
  const [autoCalculate, setAutoCalculate] = useState(true);
  const [manualCells, setManualCells] = useState({});
  const [applyChungEliteVat, setApplyChungEliteVat] = useState(false);

  const [summary, setSummary] = useState({
    kRate: 0,
    hoaHong: 0,
    elite: 0,
    thuongNong: 0,
    totalPDVAndTN: DEFAULT_CVKD_MECHANISM_RATE,
    ratePDVAndTNCVKDElite: DEFAULT_CVKD_MECHANISM_RATE,
    rateDefault: DEFAULT_PDV_RATE,
    rateEliteDefault: DEFAULT_ELITE_RATE,
    transactionRate: DEFAULT_TRANSACTION_RATE,
  });

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schemaAdd),
    defaultValues: {
      saleOpeningEventId,
      mechanismReceive: DEFAULT_MAIN_MECHANISM_RATE,
      cvkdMechanismRate: DEFAULT_CVKD_MECHANISM_RATE,
      note: "",
    },
  });

  const tableHeaders = useMemo(
    () => [
      { label: "STT", key: "stt", width: "42px" },
      { label: "Các cấp", key: "name", width: "170px" },
      { label: "Viết tắt", key: "code", width: "76px" },
      { label: "Cơ chế %", key: "mechanismRate", width: "86px" },
      { label: "Tỉ lệ", key: "rate", width: "78px" },
      { label: "Cơ chế %", key: "mechanismRateElite", width: "86px" },
      { label: "Tỉ lệ", key: "rateElite", width: "78px" },
    ],
    [],
  );

  const applySummaryToForm = (nextSummary) => {
    setValue("kRate", nextSummary.kRate);
    setValue("rate", nextSummary.hoaHong);
    setValue("rateElite", nextSummary.elite);
    setValue("rateTN", nextSummary.thuongNong);
    setValue("rateDefault", nextSummary.rateDefault);
    setValue("rateEliteDefault", nextSummary.rateEliteDefault);
    setValue("transactionRate", nextSummary.transactionRate);
  };

  const setRowsAndRecalculate = (nextRows, options = {}) => {
    const nextMode = options.cvkdRateMode || cvkdRateMode;
    const nextAutoCalculate =
      options.autoCalculate !== undefined
        ? options.autoCalculate
        : autoCalculate;
    const nextManualCells = options.manualCells || manualCells;

    const nextApplyChungEliteVat =
      options.applyChungEliteVat !== undefined
        ? options.applyChungEliteVat
        : applyChungEliteVat;

    const mechanismReceive =
      options.mechanismReceive ?? getValues("mechanismReceive");

    const cvkdMechanismRate =
      options.cvkdMechanismRate ?? getValues("cvkdMechanismRate");

    const calculated = calculateMechanism({
      inputRows: nextRows,
      mechanismReceive,
      cvkdMechanismRate,
      cvkdRateMode: nextMode,
      autoCalculate: nextAutoCalculate,
      manualCells: nextManualCells,
      applyChungEliteVat: nextApplyChungEliteVat,
    });

    setRows(calculated.rows);
    setSummary(calculated.summary);
    applySummaryToForm(calculated.summary);

    const chung = getRow(calculated.rows, CHUNG_CODE);
    const cvkd = getRow(calculated.rows, CVKD_CODE);

    if (chung && hasValue(chung.mechanismRate)) {
      setValue("mechanismReceive", rawNumber(chung.mechanismRate));
    }

    if (cvkd && hasValue(cvkd.mechanismRateElite)) {
      setValue("cvkdMechanismRate", rawNumber(cvkd.mechanismRateElite));
    }

    return calculated;
  };

  const fetchMechanism = async (mechanismId) => {
    try {
      let response;

      if (mechanismId) {
        response =
          await saleOpeningEventMechanismParametersCall.getById(mechanismId);
      }

      const defaultRows = buildDefaultRows();

      if (response && response !== true) {
        const loadedRows = Array.isArray(response?.data)
          ? response.data.map(normalizeRow)
          : defaultRows;

        const loadedChung = getRow(loadedRows, CHUNG_CODE);
        const loadedCvkd = getRow(loadedRows, CVKD_CODE);

        const nextMode =
          response?.cvkdRateMode || CVKD_RATE_MODE.INCLUDED_ELITE;

        const nextAutoCalculate =
          response?.autoCalculate !== undefined
            ? Boolean(response.autoCalculate)
            : true;

        const nextManualCells = response?.manualCells || {};

        const nextApplyChungEliteVat =
          response?.applyChungEliteVat !== undefined
            ? Boolean(response.applyChungEliteVat)
            : false;

        const nextMechanismReceive = hasValue(loadedChung?.mechanismRate)
          ? loadedChung.mechanismRate
          : response?.mechanismReceive || DEFAULT_MAIN_MECHANISM_RATE;

        const nextCvkdMechanism = hasValue(loadedCvkd?.mechanismRateElite)
          ? loadedCvkd.mechanismRateElite
          : response?.cvkdMechanismRate ||
            response?.ratePDVAndTNCVKDElite ||
            DEFAULT_CVKD_MECHANISM_RATE;

        setCvkdRateMode(nextMode);
        setAutoCalculate(nextAutoCalculate);
        setManualCells(nextManualCells);
        setApplyChungEliteVat(nextApplyChungEliteVat);

        reset({
          saleOpeningEventId,
          mechanismReceive: nextMechanismReceive,
          cvkdMechanismRate: nextCvkdMechanism,
          note: response?.note || "",
          kRate: response?.kRate,
          rate: response?.rate,
          rateElite: response?.rateElite,
          rateTN: response?.rateTN,
          transactionRate:
            response?.transactionRate || DEFAULT_TRANSACTION_RATE,
        });

        setRowsAndRecalculate(loadedRows, {
          mechanismReceive: nextMechanismReceive,
          cvkdMechanismRate: nextCvkdMechanism,
          cvkdRateMode: nextMode,
          autoCalculate: nextAutoCalculate,
          manualCells: nextManualCells,
          applyChungEliteVat: nextApplyChungEliteVat,
        });

        return;
      }

      reset({
        saleOpeningEventId,
        mechanismReceive: DEFAULT_MAIN_MECHANISM_RATE,
        cvkdMechanismRate: DEFAULT_CVKD_MECHANISM_RATE,
        note: "",
      });

      setCvkdRateMode(CVKD_RATE_MODE.INCLUDED_ELITE);
      setAutoCalculate(true);
      setManualCells({});
      setApplyChungEliteVat(false);

      setRowsAndRecalculate(defaultRows, {
        mechanismReceive: DEFAULT_MAIN_MECHANISM_RATE,
        cvkdMechanismRate: DEFAULT_CVKD_MECHANISM_RATE,
        cvkdRateMode: CVKD_RATE_MODE.INCLUDED_ELITE,
        autoCalculate: true,
        manualCells: {},
        applyChungEliteVat: false,
      });
    } catch (error) {
      console.error("Error", error);
      toast.error("Có lỗi xảy ra khi lấy cơ chế hệ số K");
    }
  };

  useEffect(() => {
    if (!saleOpeningEventId) return;

    setValue("saleOpeningEventId", saleOpeningEventId);
    fetchMechanism(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, saleOpeningEventId]);

  const handleAutoCalculateChange = (checked) => {
    setAutoCalculate(checked);

    setRowsAndRecalculate(rows, {
      autoCalculate: checked,
    });
  };

  const handleApplyChungEliteVatChange = (checked) => {
    setApplyChungEliteVat(checked);

    setRowsAndRecalculate(rows, {
      applyChungEliteVat: checked,
    });
  };

  const handleTopInputChange = (field, value) => {
    const nextValue = value === "" ? "" : clampPercent(value);

    setValue(field, nextValue);

    let nextRows = rows.map((item) => ({ ...item }));
    const nextManualCells = { ...manualCells };

    if (field === "mechanismReceive") {
      nextRows = nextRows.map((item) =>
        item.code === CHUNG_CODE
          ? {
              ...item,
              mechanismRate: nextValue,
            }
          : item,
      );

      nextManualCells[makeManualKey(CHUNG_CODE, "mechanismRate")] = true;
    }

    if (field === "cvkdMechanismRate") {
      nextRows = nextRows.map((item) =>
        item.code === CVKD_CODE
          ? {
              ...item,
              mechanismRateElite: nextValue,
            }
          : item,
      );

      nextManualCells[makeManualKey(CVKD_CODE, "mechanismRateElite")] = true;
    }

    setManualCells(nextManualCells);

    setRowsAndRecalculate(nextRows, {
      [field]: nextValue,
      manualCells: nextManualCells,
    });
  };

  const handleModeChange = (value) => {
    setCvkdRateMode(value);

    const cvkd = getRow(rows, CVKD_CODE);
    const nextCvkdMechanismRate = rawNumber(
      cvkd?.mechanismRateElite,
      DEFAULT_CVKD_MECHANISM_RATE,
    );

    setValue("cvkdMechanismRate", nextCvkdMechanismRate);

    setRowsAndRecalculate(rows, {
      cvkdRateMode: value,
      cvkdMechanismRate: nextCvkdMechanismRate,
    });
  };

  const handleCellChange = (rowId, key, value) => {
    const nextValue = value === "" ? "" : clampPercent(value);
    const targetRow = rows.find((item) => item.id === rowId);

    if (!targetRow) return;

    const nextRows = rows.map((item) =>
      item.id === rowId
        ? {
            ...item,
            [key]: nextValue,
          }
        : item,
    );

    const nextManualCells = {
      ...manualCells,
      [makeManualKey(targetRow.code, key)]: true,
    };

    setManualCells(nextManualCells);

    const options = {
      manualCells: nextManualCells,
    };

    if (targetRow.code === CHUNG_CODE && key === "mechanismRate") {
      options.mechanismReceive = nextValue;
      setValue("mechanismReceive", nextValue);
    }

    if (targetRow.code === CVKD_CODE && key === "mechanismRateElite") {
      options.cvkdMechanismRate = nextValue;
      setValue("cvkdMechanismRate", nextValue);
    }

    setRowsAndRecalculate(nextRows, options);
  };

  const buildPayload = (formData) => {
    const preparedRows = rows.map((item) => {
      if (item.code === CHUNG_CODE) {
        return {
          ...item,
          mechanismRate: rawNumber(formData.mechanismReceive),
        };
      }

      if (item.code === CVKD_CODE) {
        return {
          ...item,
          mechanismRateElite: rawNumber(formData.cvkdMechanismRate),
        };
      }

      return item;
    });

    const calculated = calculateMechanism({
      inputRows: preparedRows,
      mechanismReceive: formData.mechanismReceive,
      cvkdMechanismRate: formData.cvkdMechanismRate,
      cvkdRateMode,
      autoCalculate,
      manualCells,
      applyChungEliteVat,
    });

    const chung = getRow(calculated.rows, CHUNG_CODE);
    const cvkd = getRow(calculated.rows, CVKD_CODE);
    const mechanismName = name ? `Cơ chế ${name}` : "Cơ chế";

    return {
      saleOpeningEventId,
      name: mechanismName,
      statusKRate: AUTO_STATUS_K_RATE,
      autoCalculate,
      manualCells,
      cvkdRateMode,
      applyChungEliteVat,
      mechanismReceive: rawNumber(chung?.mechanismRate),
      cvkdMechanismRate: rawNumber(cvkd?.mechanismRateElite),
      transactionRate: DEFAULT_TRANSACTION_RATE,
      kRate: rawNumber(calculated.summary.kRate),
      rate: rawNumber(calculated.summary.hoaHong),
      rateDefault: rawNumber(calculated.summary.rateDefault),
      rateElite: rawNumber(calculated.summary.elite),
      rateEliteDefault: rawNumber(calculated.summary.rateEliteDefault),
      rateTN: rawNumber(calculated.summary.thuongNong),
      ratePDVAndTNCVKDElite: rawNumber(calculated.summary.totalPDVAndTN),
      mechanismRateEliteCHUNG: rawNumber(chung?.mechanismRateElite),
      mechanismRateEliteCHUNGForCalc: getChungMechanismRateEliteForCalc(
        chung?.mechanismRateElite,
        applyChungEliteVat,
      ).toNumber(),
      mechanismRateEliteCVKD: rawNumber(cvkd?.mechanismRateElite),
      note: formData?.note || "",
      data: calculated.rows,
    };
  };

  const submitHandlerCreate = async (data) => {
    const toastCreate = toast.loading("Đang thêm cơ chế hệ số K ...");

    try {
      const payload = buildPayload(data);

      await saleOpeningEventMechanismParametersCall.create(payload);
      await refetch();
      onToggle();
      onClose();
      toast.success("Thêm cơ chế hệ số K thành công");
    } catch (error) {
      console.log({ error });
      toast.error("Thêm cơ chế hệ số K thất bại");
    } finally {
      toast.dismiss(toastCreate);
    }
  };

  const submitHandlerEdit = async (data) => {
    const toastCreate = toast.loading("Đang sửa cơ chế hệ số K ...");

    try {
      const payload = buildPayload(data);

      await saleOpeningEventMechanismParametersCall.update(id, payload);
      await refetch();
      onToggle();
      onClose();
      toast.success("Sửa cơ chế hệ số K thành công");
    } catch (error) {
      console.log({ error });
      toast.error("Sửa cơ chế hệ số K thất bại");
    } finally {
      toast.dismiss(toastCreate);
    }
  };

  return (
    <Box w="full">
      <Flex
        onClick={onClose}
        cursor="pointer"
        gap="6px"
        alignItems="center"
        mb="10px"
        maxW="max-content"
      >
        <IoArrowBackOutline fontSize="20px" />
        <Text
          fontSize="16px"
          lineHeight="18px"
          fontWeight="600"
          color="var(--color-header-employee)"
        >
          Quay lại
        </Text>
      </Flex>

      <form
        onSubmit={handleSubmit((data) => {
          if (id) {
            submitHandlerEdit(data);
          } else {
            submitHandlerCreate(data);
          }
        })}
      >
        <Flex flexDirection="column" gap="10px" mb="14px">
          <Flex
            backgroundColor="var(--color-main)"
            borderRadius="10px"
            flexDirection="column"
            overflow="hidden"
          >
            <Box p="10px 12px 8px 12px">
              <Text
                fontSize="17px"
                lineHeight="19px"
                fontWeight="600"
                color="var(--color-header-employee)"
                mb="10px"
              >
                Đợt mở bán: {name || ""}
              </Text>

              <Flex
                justifyContent="space-between"
                alignItems="flex-start"
                gap="10px"
                flexWrap="wrap"
              >
                <Box>
                  <Text
                    fontSize="14px"
                    lineHeight="18px"
                    fontWeight="600"
                    color="var(--color-header-employee)"
                    mb="4px"
                  >
                    Cơ chế tính toán hệ số K
                  </Text>

                  <Text
                    fontSize="12px"
                    color="var(--color-label-employee)"
                    fontWeight={400}
                  >
                    Tính bằng số gốc, chỉ làm tròn khi hiển thị.
                  </Text>
                </Box>

                <Button
                  w="132px"
                  h="34px"
                  display="flex"
                  alignItems="center"
                  p="8px 12px"
                  gap="6px"
                  backgroundImage="var(--color-button)"
                  _hover={{
                    filter: "brightness(90%)",
                  }}
                  type="submit"
                >
                  <Text
                    fontSize="13px"
                    fontWeight={600}
                    color="var(--color-main)"
                  >
                    Xác nhận
                  </Text>
                </Button>
              </Flex>
            </Box>

            <Flex
              p="0 12px 10px 12px"
              gap="10px"
              alignItems="flex-end"
              flexWrap="wrap"
            >
              <Box minW="230px">
                <Text
                  mb="4px"
                  fontSize="12px"
                  fontWeight={600}
                  color="var(--color-label-employee)"
                >
                  Tỉ lệ CVKD có bao gồm Elite chưa?
                </Text>
                <Select
                  h="32px"
                  fontSize="12px"
                  bg="white"
                  value={cvkdRateMode}
                  onChange={(event) => handleModeChange(event.target.value)}
                >
                  <option value={CVKD_RATE_MODE.INCLUDED_ELITE}>
                    Đã bao gồm Elite
                  </option>
                  <option value={CVKD_RATE_MODE.NOT_INCLUDED_ELITE}>
                    Chưa bao gồm Elite
                  </option>
                </Select>
              </Box>

              <Flex
                minW="160px"
                h="32px"
                alignItems="center"
                gap="8px"
                pb="1px"
              >
                <Switch
                  size="sm"
                  isChecked={autoCalculate}
                  onChange={(event) =>
                    handleAutoCalculateChange(event.target.checked)
                  }
                />
                <Text
                  fontSize="12px"
                  fontWeight={600}
                  color="var(--color-label-employee)"
                >
                  Tự động tính
                </Text>
              </Flex>

              <Flex
                minW="160px"
                h="32px"
                alignItems="center"
                gap="8px"
                pb="1px"
              >
                <Switch
                  size="sm"
                  isChecked={applyChungEliteVat}
                  onChange={(event) =>
                    handleApplyChungEliteVatChange(event.target.checked)
                  }
                />
                <Text
                  fontSize="12px"
                  fontWeight={600}
                  color="var(--color-label-employee)"
                >
                  Cơ chế nhận về đã bao gồm VAT
                </Text>
                <Tooltip
                  label="Cơ chế nhận về sẽ bao gồm 10% VAT. Khi bật trường này, nếu bạn nhập 5 vẫn hiển thị 5 nhưng hệ thống dùng đúng 5 / 1.1 để tính tiếp, không làm tròn trong công thức"
                  hasArrow
                  placement="top"
                  fontSize="12px"
                  maxW="280px"
                >
                  <Box
                    as="span"
                    display="inline-flex"
                    alignItems="center"
                    cursor="pointer"
                    color="var(--color-label-employee)"
                  >
                    <IoInformationCircleOutline fontSize="16px" />
                  </Box>
                </Tooltip>
              </Flex>
            </Flex>

            <Flex width="100%" height="100%" alignItems="stretch">
              <TableContainer
                w="calc(100% - 360px)"
                maxH="calc(100vh - 270px)"
                overflowY="auto"
                overflowX="auto"
                borderTop="1px solid #1A202C"
              >
                <Table w="100%" variant="unstyled" size="sm">
                  <Thead
                    position="sticky"
                    top="0"
                    zIndex="1"
                    backgroundColor="#A9CF8E"
                  >
                    <Tr>
                      <Td
                        rowSpan={2}
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        w={tableHeaders[0].width}
                        p="4px"
                        fontSize="12px"
                      >
                        STT
                      </Td>
                      <Td
                        rowSpan={2}
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        minW={tableHeaders[1].width}
                        p="4px"
                        fontSize="12px"
                      >
                        Các cấp
                      </Td>
                      <Td
                        rowSpan={2}
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        minW={tableHeaders[2].width}
                        p="4px"
                        fontSize="12px"
                      >
                        Viết tắt
                      </Td>
                      <Td
                        colSpan={2}
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        p="4px"
                        fontSize="12px"
                      >
                        Áp dụng cho CVKD Elite thường
                      </Td>
                      <Td
                        colSpan={2}
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        p="4px"
                        fontSize="12px"
                      >
                        Áp dụng cho CVKD Elite
                      </Td>
                    </Tr>

                    <Tr>
                      {tableHeaders.slice(3).map((header) => (
                        <Td
                          key={header.key}
                          border="1px solid #1A202C"
                          fontWeight={700}
                          textAlign="center"
                          minW={header.width}
                          p="4px"
                          fontSize="12px"
                        >
                          {header.label}
                        </Td>
                      ))}
                    </Tr>
                  </Thead>

                  <Tbody>
                    {rows.map((row, rowIndex) => {
                      const isTopRow = row.code === CHUNG_CODE;
                      const isTotalRow = isSummaryRow(row.code);

                      return (
                        <Tr
                          key={row.id}
                          bg={
                            isTopRow
                              ? "#FFF2CC"
                              : isTotalRow
                                ? "#A9CF8E"
                                : "white"
                          }
                        >
                          <Td
                            border="1px solid #1A202C"
                            textAlign="center"
                            fontWeight={isTotalRow || isTopRow ? 700 : 500}
                            p="3px"
                            fontSize="12px"
                          >
                            {isTopRow || isTotalRow ? "" : rowIndex}
                          </Td>

                          <Td
                            border="1px solid #1A202C"
                            textAlign="left"
                            fontWeight={isTotalRow || isTopRow ? 700 : 500}
                            p="3px 5px"
                            fontSize="12px"
                          >
                            {row.name}
                          </Td>

                          <Td
                            border="1px solid #1A202C"
                            textAlign="left"
                            fontWeight={isTotalRow || isTopRow ? 700 : 500}
                            p="3px 5px"
                            fontSize="12px"
                          >
                            {row.code}
                          </Td>

                          {[
                            "mechanismRate",
                            "rate",
                            "mechanismRateElite",
                            "rateElite",
                          ].map((field) => (
                            <Td
                              key={field}
                              border="1px solid #1A202C"
                              textAlign="left"
                              fontWeight={isTotalRow || isTopRow ? 700 : 500}
                              p="3px"
                              fontSize="12px"
                            >
                              {editableCell({
                                row,
                                field,
                                value: row[field],
                                highlight:
                                  autoCalculate &&
                                  isRequiredInputCell(row.code, field),
                                onChange: (value) =>
                                  handleCellChange(row.id, field, value),
                              })}
                            </Td>
                          ))}
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>

              <Box
                w="360px"
                minW="360px"
                borderTop="1px solid #1A202C"
                borderRight="1px solid #1A202C"
                borderBottom="1px solid #1A202C"
              >
                <Box
                  bg="#A9CF8E"
                  color="red"
                  fontWeight={700}
                  textAlign="center"
                  p="8px"
                  borderBottom="1px solid #1A202C"
                  fontSize="14px"
                >
                  {name ? `Cơ chế ${name}` : "Cơ chế"}
                </Box>

                <Table w="100%" variant="unstyled" size="sm">
                  <Thead bg="#A9CF8E">
                    <Tr>
                      <Td
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        p="4px"
                        fontSize="12px"
                      >
                        Hoa hồng
                      </Td>
                      <Td
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        p="4px"
                        fontSize="12px"
                      >
                        Elite
                      </Td>
                      <Td
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        p="4px"
                        fontSize="12px"
                      >
                        Thưởng nóng
                      </Td>
                      <Td
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        p="4px"
                        fontSize="12px"
                      >
                        TỔNG
                      </Td>
                    </Tr>
                    <Tr>
                      <Td
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        p="4px"
                        fontSize="12px"
                      >
                        ({percentText(DEFAULT_PDV_RATE)})
                      </Td>
                      <Td
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        p="4px"
                        fontSize="12px"
                      >
                        ({percentText(summary.rateEliteDefault)})
                      </Td>
                      <Td
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        p="4px"
                        fontSize="12px"
                      />
                      <Td
                        border="1px solid #1A202C"
                        fontWeight={700}
                        textAlign="center"
                        p="4px"
                        fontSize="12px"
                      />
                    </Tr>
                  </Thead>

                  <Tbody>
                    <Tr bg="#FFF2CC">
                      <Td
                        border="1px solid #1A202C"
                        textAlign="center"
                        fontWeight={700}
                        p="5px"
                        fontSize="12px"
                      >
                        {percentText(summary.hoaHong)}
                      </Td>
                      <Td
                        border="1px solid #1A202C"
                        textAlign="center"
                        fontWeight={700}
                        p="5px"
                        fontSize="12px"
                      >
                        {percentText(summary.elite)}
                      </Td>
                      <Td
                        border="1px solid #1A202C"
                        textAlign="center"
                        fontWeight={700}
                        p="5px"
                        fontSize="12px"
                      >
                        {percentText(summary.thuongNong)}
                      </Td>
                      <Td
                        border="1px solid #1A202C"
                        textAlign="center"
                        fontWeight={700}
                        p="5px"
                        fontSize="12px"
                        bg="#FFFF00"
                        color="red"
                      >
                        {percentText(summary.totalPDVAndTN)}
                      </Td>
                    </Tr>

                    <Tr bg="#FFF2CC">
                      <Td
                        border="1px solid #1A202C"
                        colSpan={2}
                        fontWeight={700}
                        textAlign="center"
                        p="5px"
                        fontSize="12px"
                      >
                        Hệ số K
                      </Td>
                      <Td
                        border="1px solid #1A202C"
                        color="red"
                        fontWeight={700}
                        textAlign="center"
                        p="5px"
                        fontSize="12px"
                        bg="#FFFF00"
                      >
                        {numberText(summary.kRate, 4)}
                      </Td>
                      <Td border="1px solid #1A202C" p="5px" />
                    </Tr>
                  </Tbody>
                </Table>

                <Box p="10px">
                  <TextareaControl
                    type="textarea"
                    label="Thêm phản hồi"
                    placeholder="Nhập phản hồi"
                    errors={errors.note}
                    register={register("note")}
                    mwidth="100%"
                    height="110px"
                  />
                </Box>
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </form>
    </Box>
  );
};

export default TableUpdateProjectMechanism;
